"use client";

import React from "react";
import { ArrowRight, Clock, FileText } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
function LegalInsightsConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="flex h-80 flex-col rounded-xl border border-border bg-card p-8 shadow-sm">
            <div className="mb-6 h-6 w-24 animate-pulse rounded-md bg-muted" />
            <div className="mb-4 h-8 w-full animate-pulse rounded-md bg-muted" />
            <div className="mb-auto h-8 w-2/3 animate-pulse rounded-md bg-muted" />
            <div className="mt-8 flex items-center justify-between border-t border-border pt-6">
              <div className="h-4 w-20 animate-pulse rounded-md bg-muted" />
              <div className="h-4 w-24 animate-pulse rounded-md bg-muted" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-12 text-center">
        <FileText className="mb-4 size-8 text-muted-foreground" />
        <p className="mb-4 text-muted-foreground">Failed to load insights. Please try again later.</p>
        <Button onClick={refetch} variant="outline">
          Retry
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-12 text-center">
        <FileText className="mb-4 size-8 text-muted-foreground" />
        <p className="text-muted-foreground">No legal insights published yet.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, i) => {
      const wordCount = post.content ? post.content.split(/\s+/).length : 1000;
      const readTime = Math.max(1, Math.ceil(wordCount / 200));
      const category = post.categories && post.categories.length > 0 ? post.categories[0].name : "Strategy";
      return <article key={post.id} data-index={i} className="group relative flex flex-col rounded-xl border border-border/60 bg-gradient-to-br from-card to-muted/30 p-8 shadow-sm shadow-shadow-color/5 transition-all duration-300 hover:-translate-y-1 hover:shadow-md" style={{
        boxShadow: "inset 0 0 40px rgba(0,0,0,0.02), 0 1px 3px rgba(0,0,0,0.05)"
      }}>
            <Badge variant="secondary" className="mb-6 w-fit bg-muted/50 text-foreground hover:bg-muted/70">
              {category}
            </Badge>
            
            <h3 className="mb-auto font-sans text-xl font-semibold leading-[1.4] text-foreground md:text-2xl">
              {post.title}
            </h3>
            
            <div className="mt-8 flex items-center justify-between border-t border-border/50 pt-6">
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Clock className="size-4" />
                <span>{readTime} min read</span>
              </div>
              
              {post.link && <Link to={post.link} className="inline-flex items-center gap-1.5 font-medium text-primary transition-colors hover:text-primary/80">
                  Read More
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                </Link>}
            </div>
          </article>;
    })}
    </div>;
}
export default function LegalInsights() {
  const wp_query_params = {
    per_page: 3,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="legal-insights" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <h2 className="mb-4 font-sans text-3xl font-bold tracking-tight text-foreground md:text-4xl">
              Legal Insights & Strategy
            </h2>
            <p className="text-lg text-muted-foreground">
              Expert perspectives on trademark acquisition, brand protection, and market entry strategies.
            </p>
          </div>
          <Link to="/OurCataloguePage" className="inline-flex items-center gap-2 font-medium text-foreground hover:text-primary">
            View All Articles
            <ArrowRight className="size-4" />
          </Link>
        </div>

        <PostsProvider wp_query={{
        per_page: 3,
        orderby: "date",
        order: "desc"
      }}>
          <LegalInsightsConsumer />
        </PostsProvider>
      </div>
    </section>;
}